/**
 * D33：发版后旧页面引用的分包已不存在时（动态导入/预加载 404），自动重新加载一次取得新版本；
 * 同一标签页 30 秒内再次失败则不再刷新，显示明确的错误提示，避免循环刷新。
 * 标记只存本标签页 sessionStorage 的时间戳，不含业务数据；存储不可用时不自动刷新。
 */
const MARKER = 'aiprint.chunk-reload-at';
const WINDOW_MS = 30_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError/i;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  return CHUNK_ERROR.test(message);
}

export interface RecoveryEnvironment {
  now(): number;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload(target?: string): void;
}

/** Returns true when a one-time reload was started; false means show the failure instead. */
export function recoverFromChunkError(
  target: string | undefined,
  env: RecoveryEnvironment,
): boolean {
  if (!env.storage) return false;
  try {
    const last = Number(env.storage.getItem(MARKER));
    const now = env.now();
    if (Number.isFinite(last) && last > 0 && now - last < WINDOW_MS) return false;
    env.storage.setItem(MARKER, String(now));
  } catch {
    return false;
  }
  env.reload(target);
  return true;
}

export function browserRecoveryEnvironment(): RecoveryEnvironment {
  let storage: RecoveryEnvironment['storage'] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    now: () => Date.now(),
    storage,
    reload: (target) => {
      if (target && target !== location.pathname + location.search + location.hash)
        location.assign(target);
      else location.reload();
    },
  };
}

export function showChunkFailure() {
  const app = document.querySelector('#app');
  if (!app) return;
  const message = document.createElement('p');
  message.textContent =
    '页面资源已更新或暂时无法加载。请按 Ctrl+F5 强制刷新；若仍失败，请联系管理员检查站点发布。';
  const retry = document.createElement('button');
  retry.textContent = '重新加载';
  retry.addEventListener('click', () => location.reload());
  app.replaceChildren(message, retry);
}

/** Wire both Vite's preload failure event and router lazy-route failures. */
export function installChunkRecovery(router: {
  onError(handler: (error: unknown, to: { fullPath: string }) => void): void;
}) {
  const env = browserRecoveryEnvironment();
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    if (!recoverFromChunkError(undefined, env)) showChunkFailure();
  });
  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;
    if (!recoverFromChunkError(to?.fullPath, env)) showChunkFailure();
  });
}
